import moment, { Moment } from "moment";
import { CreateUpdateResponsePayload, QResponse, QuartersResponse } from "../model/ipmsmodel";
import { GoalsFlagStatusEnum } from "./constant";
import { format } from "date-fns";

interface GroupedGoals {
  [quarter: number]: number[];
}

export const convertGoalsToGroupedIds = (goals: QResponse[]): GroupedGoals => {
  return goals.reduce<GroupedGoals>((acc, goal) => {
    if (goal?.id !== null) {
      // Initialize the array for this quarter if it doesn't exist
      if (!acc[goal.quater]) {
        acc[goal.quater] = [];
      }

      // Ensure the goal ID is unique within the quarter
      if (!acc[goal.quater].includes(goal.id)) {
        acc[goal.quater].push(goal.id);
      }
    }

    return acc;
  }, {} as GroupedGoals);
};

const quarterMapping: Record<string, number> = {
  q1: 1,
  q2: 2,
  q3: 3,
  q4: 4,
};

export const convertSaveUpdateGoalsToPayload = (
  submitstatus: number,
  startYear: number,
  employeeidData: number,
  data: QuartersResponse,
  deletedQuarters: QResponse[],
  assessmentStart: string,
  assessmentEnd: string,
  onlyQuarters: string,
  approver_id: string,
  rejectFunctionFlag: boolean
): CreateUpdateResponsePayload => {
  const payload: CreateUpdateResponsePayload = {
    quarters_data: {},
    start_date: assessmentStart,
    end_date: assessmentEnd,
    approver_id: Number(approver_id)
  };
  const deleteData = convertGoalsToGroupedIds(deletedQuarters);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const removeChangesType = ({ changesType, ...rest }: QResponse): Omit<QResponse, "changesType"> => {
    return rest;
  };
  if (onlyQuarters) {
    const onlyQuartersKey = (onlyQuarters || "").toLowerCase();
    for (const quarter in data) {
      // console.log(onlyQuarters, quarter);
      if (onlyQuartersKey === quarter.toLowerCase() && Object.prototype.hasOwnProperty.call(data, quarter)) {
        const goals = data[quarter];
        const quarterNumber = quarterMapping[quarter];

        // if (quarterNumber === undefined) {
        //   // console.warn(`Quarter mapping for ${quarter} is undefined.`);
        //   continue; // Skip this iteration if the quarter is not mapped
        // }

        // console.log('startYear ==> ', startYear)
        // Update each goal's `quater` field to the correct numeric value
        const updatedGoals = goals.map((goal) => ({
          ...goal,
          // startyear: startYear ? startYear : new Date().getFullYear(),
          startyear: startYear,
          quater: quarterNumber,
          employeeid: employeeidData,
          submissionstatus: rejectFunctionFlag ? submitstatus :
            [GoalsFlagStatusEnum.approved, GoalsFlagStatusEnum.rejected].includes(
              goal.submissionstatus
            )
              ? goal.submissionstatus
              : submitstatus,
        }));
        // console.log("updatedGoals", updatedGoals, rejectFunctionFlag , submitstatus);
        payload.quarters_data[quarter] = {
          insert: updatedGoals.filter((goal) => goal.changesType === "new").map(removeChangesType),
          update: updatedGoals
            .filter(
              (goal) =>
                (goal.id !== null && goal.changesType === "update") ||
                (goal.id !== null && goal.submissionstatus === GoalsFlagStatusEnum.save) ||
                (goal.id !== null && goal.submissionstatus === GoalsFlagStatusEnum.submit),
            )
            .map(removeChangesType),
          delete: deleteData[quarterNumber] ?? [],
        };
      }
    }
  }
  if (!onlyQuarters) {
    for (const quarter in data) {
      if (Object.prototype.hasOwnProperty.call(data, quarter)) {
        const goals = data[quarter];
        const quarterNumber = quarterMapping[quarter];

        // if (quarterNumber === undefined) {
        //   // console.warn(`Quarter mapping for ${quarter} is undefined.`);
        //   continue; // Skip this iteration if the quarter is not mapped
        // }
        // console.log('startYear ==> ', startYear)
        // Update each goal's `quater` field to the correct numeric value
        const updatedGoals = goals.map((goal) => ({
          ...goal,
          startyear: startYear,
          quater: quarterNumber,
          employeeid: employeeidData,
          submissionstatus: rejectFunctionFlag
            ? submitstatus
            : [GoalsFlagStatusEnum.approved, GoalsFlagStatusEnum.rejected].includes(
                goal.submissionstatus
              )
            ? goal.submissionstatus
            : submitstatus,
        }));
        //  console.log("updatedGoals 2", updatedGoals, rejectFunctionFlag , submitstatus);
        payload.quarters_data[quarter] = {
          insert: updatedGoals.filter((goal) => goal.changesType === "new").map(removeChangesType),
          update: updatedGoals
            .filter(
              (goal) =>
                (goal.id !== null && goal.changesType === "update") ||
                (goal.id !== null && goal.submissionstatus === GoalsFlagStatusEnum.save) ||
                (goal.id !== null && goal.submissionstatus === GoalsFlagStatusEnum.submit),
            )
            .map(removeChangesType),
          delete: deleteData[quarterNumber] ?? [],
        };
      }
    }
  }
  return payload;
};

export type DateRange = {
  start: Moment | null;
  end: Moment | null;
};

export const getQuarterRanges = (startDate: Moment, endDate: Moment): DateRange[] => {
  const quarterRanges: DateRange[] = [];
  let currentStartDate = startDate.clone();

  while (currentStartDate.isBefore(endDate)) {
    let currentEndDate = currentStartDate.clone().add(3, "months");

    if (currentEndDate.isAfter(endDate)) {
      currentEndDate = endDate.clone();
    }

    quarterRanges.push({ start: currentStartDate, end: currentEndDate });
    currentStartDate = currentEndDate.clone();
  }

  return quarterRanges;
};

// Function to format the month and year as "mmm yyyy"
const formatMonthYear = (date: Date): { label: string; value: string } => {
  const value = moment(date).format("YYYY-MM-DD");
  const month = date.toLocaleString("default", { month: "short" });
  const year = date.getFullYear();
  return { label: `${month} ${year}`, value: value };
};

// Function to generate an array of formatted month names for a date range
export const generateMonthArray = (startDate: Date, endDate: Date): { label: string; value: string }[] => {
  const monthArray: { label: string; value: string }[] = [];
  let currentDate = new Date(startDate);

  while (currentDate <= endDate) {
    monthArray.push(formatMonthYear(currentDate));

    // Manually update the month
    const nextMonth = currentDate.getMonth() + 1;
    currentDate = new Date(currentDate.getFullYear(), nextMonth, 1);
  }
  return monthArray;
};

export const validateTime = (time: string, date: string): string | null => {
  const [hours, minutes] = time.split(":").map(Number);
  const isTimeInvalid = hours > 24 || (hours === 24 && minutes > 0);

  if (isTimeInvalid) {
    return `Total number of hours on ${date} should not be greater than 24:00 hours.`;
  }

  return null;
};

// report healper
//
export const formatAsSevenDigitNumber = (id: number) => {
  return id ? id.toString().padStart(7, "0") : "";
};

export const getSameDateThisYear = (dateString: string, year: number): string => {
  const [day, monthName] = dateString.split(" ");
  const month = new Date(Date.parse(monthName + " 1, 2020")).getMonth(); // Get month index

  // Create a new date object with the provided year
  const newDate = new Date(year, month, parseInt(day, 10));

  // Format the new date as 'dd MMMM yyyy'
  return format(newDate, "yyyy-MM-dd");
};

export const generateWeeksInMonth = (selectedDate: Date, userID: number) => {
  const startOfMonth = moment(selectedDate).startOf("month");
  const endOfMonth = moment(selectedDate).endOf("month");

  const weeks = [];
  const currentWeekStart = startOfMonth.clone().startOf("week");

  while (currentWeekStart.isBefore(endOfMonth)) {
    const weekStart = currentWeekStart.clone();
    const weekEnd = currentWeekStart.clone().endOf("week");

    weeks.push({
      start: weekStart.toDate(),
      end: weekEnd.toDate(),
      userId: userID,
    });

    currentWeekStart.add(1, "week");
  }

  return weeks;
};


